
import { defineComponent, onBeforeMount, reactive } from 'vue'

// components
import PageTitle from '@/common/components/UI/PageTitle/index.vue'
import TheInput from '@/common/components/UI/TheInput/index.vue'
import TheButton from '@/common/components/UI/TheButton/index.vue'
import Checkbox from '@/common/components/UI/Checkbox/index.vue'
import TheSelect from '@/common/components/UI/TheSelect/index.vue'

// types
import { RPKPayloadT, RPKT } from '@/core/types/rpk.types'
import { CashFlowT } from '@/core/types/rpp.types'
import { CarrierT } from '@/core/types/carriers.types'

// realisations
import { rpk } from '@/core/realisations/rpk.realisation'
import { carriers } from '@/core/realisations/carriers.realisation'

// composable
import { useRoute, useRouter } from 'vue-router'
import useNotifications from '@/common/composable/useNotifications'

// validation
import { required } from '@vuelidate/validators'
import useVuelidate from '@vuelidate/core'

// classes
import { DataState } from '@/core/classes/DataState.class'
import { rpp } from '@/core/realisations/rpp.realisation'

export default defineComponent({
    components: {
        'page-title': PageTitle,
        'the-select': TheSelect,
        'the-input': TheInput,
        'the-button': TheButton,
        checkbox: Checkbox,
    },
    setup() {
        const state = reactive({
            is_loading: false,
            rpk: null as RPKT | null,
            // rpk
            cash_flow_id: null as number | null,
            act_number: null as string | null,
            act_date: null as string | null,
            delivery_date: null as string | null,
            pbc_date: null as string | null,
            with_income_tax_6: false,
            with_income_tax_7: false,
            with_nds_tax: 0 as number,
            company_id: null as number | null,
        })

        const data_state = reactive({
            cash_flows: new DataState<CashFlowT>({}),
            carriers: new DataState<CarrierT>({}),
        })

        const route = useRoute()
        const router = useRouter()
        const rpk_id = Number(route.params.id)
        const notifications = useNotifications()

        const EditRPKValidation = {
            cash_flow_id: { required },
            act_number: { required },
            act_date: { required },
            pbc_date: { required },
            company_id: { required },
        }

        const state_form$ = useVuelidate(EditRPKValidation, state)

        const setRPK = () => {
            if (state.rpk) {
                state.cash_flow_id = state.rpk.cash_flow_id
                state.act_number = state.rpk.act_number
                state.act_date = state.rpk.act_date
                state.delivery_date = state.rpk.delivery_date
                state.pbc_date = state.rpk.pbc_date
                state.with_nds_tax = state.rpk.with_nds_tax || 0
                state.company_id = state.rpk.company_id

                if (state.rpk.income_tax) {
                    if (state.rpk.income_tax.id === 1) {
                        state.with_income_tax_6 = true
                    } else if (state.rpk.income_tax.id === 2) {
                        state.with_income_tax_7 = true
                    }
                }
            }
        }

        const loadRPK = async () => {
            try {
                state.is_loading = true
                const response = await rpk.getRPK(rpk_id)

                state.rpk = response.data
                setRPK()
            } catch (error) {
                console.log(error)
            } finally {
                state.is_loading = false
            }
        }

        const editRPK = async () => {
            state_form$.value.$touch()
            if (state_form$.value.$invalid) return

            try {
                state.is_loading = true

                const payload: RPKPayloadT = {
                    id: rpk_id,
                    cash_flow_id: state.cash_flow_id,
                    act_number: state.act_number,
                    act_date: state.act_date,
                    delivery_date: state.delivery_date,
                    pbc_date: state.pbc_date,
                    with_nds_tax: state.with_nds_tax,
                    company_id: state.company_id,
                }

                if (state.with_income_tax_6) {
                    payload.income_tax_id = 1
                }
                if (state.with_income_tax_7) {
                    payload.income_tax_id = 2
                }

                await rpk.editRPK(payload)

                notifications.notify({
                    title: `РПК успешно отредактировано`,
                    type: 'success',
                })
                await router.push('/rpk')
            } catch (error) {
                notifications.notifyError({
                    error,
                    title: 'При редактировании РПК произошла ошибка',
                })
            } finally {
                state.is_loading = false
            }
        }

        const loadCashFlows = async () => {
            data_state.cash_flows.pagination.current_page = 1

            try {
                data_state.cash_flows.is_loading = true
                const response = await rpp.getCashFlows()
                data_state.cash_flows.list = response.data

                data_state.cash_flows.pagination.total_pages = 1

                data_state.cash_flows.is_failed = false
                data_state.cash_flows.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.cash_flows.failure.catchError(error)

                if (!data_state.cash_flows.failure.error_state.is_cancel) {
                    data_state.cash_flows.is_failed = true
                }
            } finally {
                if (!data_state.cash_flows.failure.error_state.is_cancel) {
                    data_state.cash_flows.is_loading = false
                }
            }
        }

        // companies

        const loadCarriers = async (page: number, search?: string) => {
            data_state.carriers.pagination.current_page = page

            try {
                const params = {
                    page,
                    search,
                    getAllCarriers: 1,
                    limit: 9999,
                    statusId: 2,
                }

                data_state.carriers.is_loading = true
                const data = await carriers.getList(params)

                data_state.carriers.list = data.data
                data_state.carriers.pagination.total_pages = data.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const loadMoreCarriers = async (page: number) => {
            if (page > data_state.carriers.pagination.total_pages) return
            data_state.carriers.pagination.current_page = page

            try {
                const params = {
                    page,
                    getAllCarriers: 1,
                    limit: 9999,
                    statusId: 2,
                }

                data_state.carriers.is_loading = true

                const response = await carriers.getList(params)
                data_state.carriers.list = data_state.carriers.list.concat(response.data)

                data_state.carriers.pagination.total_pages = response.meta.last_page

                data_state.carriers.is_failed = false
                data_state.carriers.failure.error_state.is_cancel = false
            } catch (error) {
                data_state.carriers.failure.catchError(error)

                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_failed = true
                }
            } finally {
                if (!data_state.carriers.failure.error_state.is_cancel) {
                    data_state.carriers.is_loading = false
                }
            }
        }

        const searchCarriers = (search_query: string) => {
            loadCarriers(1, search_query)
        }

        const unshiftFindedCarrier = async (carrier: CarrierT) => {
            await loadCarriers(data_state.carriers.pagination.current_page)
            data_state.carriers.list.unshift(carrier)
        }

        const onChangeTaxSix = (value: boolean) => {
            if (value) state.with_income_tax_7 = false
            else if (!state.with_income_tax_7) {
                state.with_nds_tax = 0
            }
        }

        const onChangeTaxSeven = (value: boolean) => {
            if (value) state.with_income_tax_6 = false
            else if (!state.with_income_tax_6) {
                state.with_nds_tax = 0
            }
        }

        // companies

        onBeforeMount(() => {
            loadCashFlows()
            loadRPK()
            loadCarriers(1)
        })

        return {
            state,
            data_state,
            state_form$,
            editRPK,
            loadCashFlows,
            //
            loadCarriers,
            loadMoreCarriers,
            searchCarriers,
            unshiftFindedCarrier,
            onChangeTaxSix,
            onChangeTaxSeven,
        }
    },
})
